import React, { Suspense, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { isRebranding } from 'reactApp/appHelpers/featuresHelpers';
import { ALL_DOCUMENTS_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { useDarkTheme } from 'reactApp/hooks/useDarkTheme';
import { usePortal } from 'reactApp/hooks/usePortal';
import { useGlobalRebranding } from 'reactApp/hooks/useRebranding';
import { ALL_DOCUMENTS_DOMAIN_ROUTES } from 'reactApp/modules/allDocuments/allDocuments.constants';
import { getHomeList } from 'reactApp/modules/home/selector/getHomeList';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { ThemeContext } from 'reactApp/modules/theme/theme.context';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { AllDocumentsPage } from 'reactApp/sections/AllDocuments/AllDocumentsPage';
import { MainPage } from 'reactApp/sections/MainPage/MainPage';
import { MobileDocumentsPage } from 'reactApp/sections/MobileDocumentsPage/MobileDocumentsPage';
import { MobileSearchPage } from 'reactApp/sections/MobileSearchPage/MobileSearchPage';
import { SearchPage } from 'reactApp/sections/SearchPage/SearchPage';
import { EPageId } from 'reactApp/types/Page';
import { CommonRootComponents } from 'reactApp/ui/App/components/CommonRootComponents';
import { NavigationSetter } from 'reactApp/ui/App/components/NavigationSetter';
import { PageWrapper } from 'reactApp/ui/App/components/PageWrapper';
import { useDataContainer } from 'reactApp/ui/App/effect/useDataContainer';
import { TooltipProvider } from 'reactApp/ui/Tooltip/Tooltip.context';
import { noop } from 'reactApp/utils/helpers';
import { getInitialId } from 'reactApp/utils/urlHelper';

const ReactViewerConnected = React.lazy(() => import(/* webpackChunkName: "reactViewer" */ '../../ui/ReactViewer/ReactViewer'));
const Editor = React.lazy(() => import(/* webpackChunkName: "Editor" */ '../../ui/Editor/Editor'));

const renderViewer = ({ selectedViewItemId, reactViewerTarget }) => {
    // Для печати просмотрщика скрываем страницу, а его рендерим в корень
    return createPortal(
        <ReactViewerConnected
            file={{ id: selectedViewItemId }}
            isPublic={false}
            isMountedFolder={false}
            showEditorHeader
            showNotify={noop}
            gaCategory="fv"
            isViewer
            enablePreload
        />,
        reactViewerTarget
    );
};

// eslint-disable-next-line max-lines-per-function
export const DocumentsRouter = () => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { isPhone, hasError } = useDataContainer();

    const reactViewerTarget = usePortal('ReactViewer');
    const selectedViewItemId = useSelector(ViewerSelectors.getViewerItemId);

    const isDarkTheme = useSelector(isCurrentDarkTheme);

    const homeList = useSelector(getHomeList);
    const hasDocumentFolder = !!homeList[`/${ALL_DOCUMENTS_FOLDER_ID}`];
    const wasDocumentFolderInitially = useRef(hasDocumentFolder);

    const ignorePaths = !wasDocumentFolderInitially.current ? [`/home/${ALL_DOCUMENTS_FOLDER_ID}`] : undefined;

    useGlobalRebranding(isRebranding);
    useDarkTheme(isDarkTheme);

    return (
        <ThemeContext.Provider value={{ isDark: isDarkTheme }}>
            <TooltipProvider>
                <Router>
                    <NavigationSetter />
                    <Suspense fallback={null}>
                        <CommonRootComponents onIsLoading={setIsLoading} ignorePaths={ignorePaths} />
                        {!isPhone && !!selectedViewItemId && renderViewer({ selectedViewItemId, reactViewerTarget })}
                        <Routes>
                            <Route
                                path={ALL_DOCUMENTS_DOMAIN_ROUTES.root}
                                element={<Navigate replace to={`${ALL_DOCUMENTS_DOMAIN_ROUTES.document}${window.location.search}`} />}
                            />
                            <Route
                                path="/:documentType"
                                element={
                                    <PageWrapper
                                        isLoading={isLoading}
                                        hasError={hasError}
                                        pageId={EPageId.alldocuments}
                                        hideHeader={isPhone}
                                    >
                                        {isPhone ? <MobileDocumentsPage /> : <AllDocumentsPage />}
                                    </PageWrapper>
                                }
                            />
                            <Route
                                path="/search/*"
                                element={
                                    <PageWrapper isLoading={isLoading} hasError={hasError} pageId={EPageId.search}>
                                        {isPhone ? (
                                            <MobileSearchPage />
                                        ) : (
                                            <MainPage storage={EStorageType.search} renderWorm>
                                                <SearchPage initialItemId={getInitialId(location?.pathname)} />
                                            </MainPage>
                                        )}
                                    </PageWrapper>
                                }
                            />
                            {['/myoffice/*', '/r-seven/*', '/r7w/*', '/office/*'].map((path) => (
                                <Route
                                    key={path}
                                    path={path}
                                    element={
                                        <PageWrapper
                                            isLoading={isLoading}
                                            hasError={hasError}
                                            pageId={EPageId.editor}
                                            hideHeader
                                            layout="flex"
                                        >
                                            <Editor storage={EStorageType.home} />
                                        </PageWrapper>
                                    }
                                />
                            ))}
                            {['/doc/word/*', '/doc/cell/*', '/doc/slide/*'].map((path) => (
                                <Route
                                    key={path}
                                    path={path}
                                    element={
                                        <PageWrapper
                                            layout="flex"
                                            isLoading={isLoading}
                                            hasError={hasError}
                                            pageId={EPageId.editor}
                                            hideHeader
                                        >
                                            <Editor storage={EStorageType.home} />
                                        </PageWrapper>
                                    }
                                />
                            ))}
                            <Route path="*" element={<Navigate replace to={ALL_DOCUMENTS_DOMAIN_ROUTES.document} />} />
                        </Routes>
                    </Suspense>
                </Router>
            </TooltipProvider>
        </ThemeContext.Provider>
    );
};
